import { useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  PagerInput,
  SignalGroupMemberAddInput,
  SignalGroupMemberDeleteInput,
  SignalMonitoringRecipientsUpsertInput,
  SignalSettingInput,
  SignalUserTeamUpsertInput,
  SignalUserTeamMembersUpsertInput
} from "gql/graphql";

export const useGetTerms = () => {
  const [result] = useQuery({
    query: /* GraphQL */ `
      query GetTerms {
        getTerms {
          termsId
          content
          contentEn
          button
          buttonEn
          updateDate
        }
      }
    `,
  });
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
  };
}

export const useSignalMonitoringSettings = () => {
  const [variables, setVariables] = useState<{ PagerInput?: PagerInput }>({});
  const [result, executeQuery] = useQuery({
    query: /* GraphQL */ `
      query getSignalMonitoringSettings($PagerInput: PagerInput) {
        getSignalMonitoringSettings(pager: $PagerInput) {
          settings {
            id
            active
            signal_type
            notice_type
            name
            categories
            areas
            channels
            target_days
            target_brand_name
            threshold_level
            min_threshold
            max_threshold
            created_at
            updated_at
          }
          totalCount
        }
      }
    `,
    variables,
    pause: !variables.PagerInput,
  });

  const fetchSignalMonitoringSettings = (pager: PagerInput) => {
    const next = { PagerInput: pager };
    setVariables(next);
    executeQuery({ variables: next, requestPolicy: "network-only" });
  }

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    fetchSignalMonitoringSettings,
  };
}

export const useGetSignalMonitoringRecipients = () => {
  const [variables, setVariables] = useState<{ signal_setting_id?: string }>({});
  const [result, executeQuery] = useQuery({
    query: /* GraphQL */ `
      query GetSignalMonitoringRecipients($signal_setting_id: ID!) {
        getSignalMonitoringRecipients(signal_setting_id: $signal_setting_id) {
          id
          type
          recipient_model_id
          created_at
          updated_at
        }
      }
    `,
    variables,
    pause: !variables.signal_setting_id,
  });

  const fetchSignalMonitoringRecipients = (signalSettingId: string) => {
    const next = { signal_setting_id: signalSettingId };
    setVariables(next);
    executeQuery({ variables: next, requestPolicy: "network-only" });
  }

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    fetchSignalMonitoringRecipients,
  };
}

export const getAthenaIShopChainsQuery = /* GraphQL */ `
  query GetAthenaIShopChains {
    getAthenaIShopChains {
      code
      name
    }
  }
`;

export const getSignalGroupCategoriesBrandsQuery = /* GraphQL */ `
  query GetSignalGroupCategoriesBrands {
    getAthenaSignalGroupCategoriesBrands {
      lv1name
      lv2name
      lv3name
      lv4name
      brand_name
    }
  }
`;

export const getSignalBrandSalesAmountQuery = /* GraphQL */ `
  query GetSignalBrandSalesAmount(
    $data: SignalBrandMonitoringConditionInput!
    $amount_type: String!
  ) {
    getSignalBrandSalesAmount(data: $data, amount_type: $amount_type) {
      comperetive_brand_results {
        brand_name
        brand_sales_amount
        term
      }
      target_brand_result {
        brand_name
        brand_sales_amount
        term
      }
    }
  }
`;

export const getSignalBrandSalesRateQuery = /* GraphQL */ `
  query GetSignalBrandSalesRate(
    $data: SignalBrandMonitoringConditionInput!
  ) {
    getSignalBrandSalesRate(data: $data) {
      comperetive_brand_results {
        brand_name
        brand_rate
        term
      }
      target_brand_result {
        brand_name
        brand_rate
        term
      }
    }
  }
`;

export const getBrandShareRateQuery = /* GraphQL */ `
  query GetSignalBrandShareRate(
    $data: SignalBrandMonitoringConditionInput!
  ) {
    getSignalBrandShareRate(data: $data) {
      comperetive_brand_results {
        brand_name
        brand_rate
        term
      }
      target_brand_result {
        brand_name
        brand_rate
        term
      }
    }
  }
`;

export const useSignalGroupMembers = () => {
  const [variables, setVariables] = useState<{ PagerInput?: PagerInput }>({});
  const [result, executeQuery] = useQuery({
    query: /* GraphQL */ `
      query getSignalGroupMembers($PagerInput: PagerInput) {
        getSignalGroupMembers(pager: $PagerInput) {
          members {
            user_id
            email
            first_name
            last_name
            signal_group_id
            signal_group_member_id
            member_type
            service_agreement
            created_at
            updated_at
          }
          totalCount
        }
      }
    `,
    variables,
  });

  const fetchSignalGroupMembers = (pager: PagerInput) => {
    const next = { PagerInput: pager };
    setVariables(next);
    executeQuery({ variables: next, requestPolicy: "network-only" });
  }

  const fetchSignalGroupMembersAll = () => {
    const next = {};
    setVariables(next);
    executeQuery({ variables: next, requestPolicy: "network-only" });
  }

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    fetchSignalGroupMembers,
    fetchSignalGroupMembersAll,
  };
}

export const useGetSignalUserTeams = () => {
  const [variables, setVariables] = useState<{ PagerInput?: PagerInput }>({});
  const [result, executeQuery] = useQuery({
    query: /* GraphQL */ `
      query GetSignalUserTeams($PagerInput: PagerInput) {
        getSignalUserTeams(pager: $PagerInput) {
          teams {
            id
            signal_group_id
            name
            created_at
            updated_at
          }
          totalCount
        }
      }
    `,
    variables,
  });

  const fetchSignalUserTeams = (pager: PagerInput) => {
    const next = { PagerInput: pager };
    setVariables(next);
    executeQuery({ requestPolicy: 'network-only', variables: next });
  }

  const fetchSignalUserTeamsAll = () => {
    const next = {};
    setVariables(next);
    executeQuery({ requestPolicy: 'network-only', variables: next });
  }

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    fetchSignalUserTeams,
    fetchSignalUserTeamsAll,
  };
}

export const useGetSignalUserTeamMembers = (userTeamId: number) => {
  const [variables, setVariables] = useState<number | null>(null);
  const [result, executeQuery] = useQuery({
    query: /* GraphQL */ `
      query GetSignalUserTeamMembers($userTeamId: ID!) {
        getSignalUserTeamMembers(signal_user_team_id: $userTeamId) {
          user_id
          email
          first_name
          last_name
          signal_group_id
          signal_group_member_id
          member_type
          service_agreement
          created_at
          updated_at
        }
      }
    `,
    variables: {
      userTeamId: userTeamId
    },
    pause: variables === null,
    requestPolicy: "network-only",
  });

  const fetchSignalUserTeamMembers = (userTeamId: number) => {
    setVariables(userTeamId);
    executeQuery({ requestPolicy: 'network-only', variables: { userTeamId: userTeamId } });
  }

  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    fetchSignalUserTeamMembers,
  };
}

export const useAgreeSignalTermsOfService = () => {
  const [_, agreeSignalTermsOfService] = useMutation<{ agreeSignalTermsOfService: string }, { group_member_id: string }>(
    `
      mutation AgreeSignalTermsOfService($group_member_id: ID!) {
        agreeSignalTermsOfService(group_member_id: $group_member_id)
      }
    `
  );

  return { agreeSignalTermsOfService };
};

export const useSaveSignalSetting = () => {
  const [_, saveSignalSetting] = useMutation<{ saveSignalSetting: string }, { data: SignalSettingInput }>(
    `
      mutation SaveSignalSetting($data: SignalSettingInput!) {
        saveSignalSetting(data: $data)
      }
    `
  );

  return { saveSignalSetting };
};

export const useSaveSignalMonitoringRecipients = () => {
  const [_, saveSignalMonitoringRecipients] = useMutation<
    { saveSignalMonitoringRecipients: string },
    { data: SignalMonitoringRecipientsUpsertInput }
  >(
    `
      mutation SaveSignalMonitoringRecipients($data: SignalMonitoringRecipientsUpsertInput!) {
        saveSignalMonitoringRecipients(data: $data)
      }
    `
  );

  return { saveSignalMonitoringRecipients };
};

export const useDeleteSignalSetting = () => {
  const [_, deleteSignalSetting] = useMutation<{ deleteSignalSetting: string }, { id: string }>(
    `
      mutation DeleteSignalSetting($id: ID!) {
        deleteSignalSetting(id: $id)
      }
    `
  );

  return { deleteSignalSetting };
};

export const useToggleSignalSettingActive = () => {
  const [_, toggleSignalSettingActive] = useMutation<{ toggleSignalSettingActive: string }, { id: string }>(
    `
      mutation ToggleSignalSettingActive($id: ID!) {
        toggleSignalSettingActive(id: $id)
      }
    `
  );

  return { toggleSignalSettingActive };
}

export const useAddSignalGroupMember = () => {
  const [_, addSignalGroupMember] = useMutation<{ addSignalGroupMember: string }, { data: SignalGroupMemberAddInput }>(
    `
      mutation AddSignalGroupMember($data: SignalGroupMemberAddInput!) {
        addSignalGroupMember(data: $data)
      }
    `
  );

  return { addSignalGroupMember };
};

export const useDeleteSignalGroupMember = () => {
  const [_, deleteSignalGroupMember] = useMutation<{ deleteSignalGroupMember: string }, { data: SignalGroupMemberDeleteInput }>(
    `
      mutation DeleteSignalGroupMember($data: SignalGroupMemberDeleteInput!) {
        deleteSignalGroupMember(data: $data)
      }
    `
  );

  return { deleteSignalGroupMember };
};

export const useSaveSignalUserTeam = () => {
  const [_, saveSignalUserTeam] = useMutation<{ saveSignalUserTeam: string }, { data: SignalUserTeamUpsertInput }>(
    `
      mutation SaveSignalUserTeam($data: SignalUserTeamUpsertInput!) {
        saveSignalUserTeam(data: $data)
      }
    `
  );

  return { saveSignalUserTeam };
};

export const useDeleteSignalUserTeam = () => {
  const [_, deleteSignalUserTeam] = useMutation<{ deleteSignalUserTeam: string }, { id: string }>(
    `
      mutation DeleteSignalUserTeam($id: ID!) {
        deleteSignalUserTeam(id: $id)
      }
    `
  );

  return { deleteSignalUserTeam };
};

export const useSaveSignalUserTeamMembers = () => {
  const [_, saveSignalUserTeamMembers] = useMutation<{ saveSignalUserTeamMembers: string }, { data: SignalUserTeamMembersUpsertInput }>(
    `
      mutation SaveSignalUserTeamMembers($data: SignalUserTeamMembersUpsertInput!) {
        saveSignalUserTeamMembers(data: $data)
      }
    `
  );

  return { saveSignalUserTeamMembers };
};
